import React from 'react';
import SEOHead from '../components/SEOHead';
import Header from '../components/Header';
import Footer from '../components/Footer';
import EstimationForm from '../components/EstimationForm';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Calculator, 
  FileText, 
  TrendingUp, 
  Shield, 
  Clock,
  CheckCircle,
  Phone,
  Mail,
  Anchor,
  ClipboardList,
  BarChart3
} from 'lucide-react';
import { InlineWidget } from 'react-calendly';
import axios from 'axios';
import { useEffect, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EstimationBateau = () => {
  const [calendlyUrl, setCalendlyUrl] = useState(null);
  const [configLoaded, setConfigLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/analyse-annonce/config`)
      .then((r) => setCalendlyUrl(r.data?.calendly_url || null))
      .catch(() => setCalendlyUrl(null))
      .finally(() => setConfigLoaded(true));
  }, []);

  const avantages = [
    {
      icon: Calculator,
      title: "Estimation précise",
      description: "Évaluation basée sur l'état réel de votre bateau, le marché actuel et les transactions récentes"
    },
    {
      icon: BarChart3,
      title: "Analyse du marché",
      description: "Comparaison avec des bateaux similaires vendus récemment pour un prix juste"
    },
    {
      icon: ClipboardList,
      title: "Rapport détaillé",
      description: "Vous recevez un document complet avec l'estimation et les arguments pour justifier le prix"
    },
    {
      icon: TrendingUp,
      title: "Conseils d'optimisation",
      description: "Recommandations pour augmenter la valeur de votre bateau si nécessaire"
    }
  ];

  const etapes = [
    {
      number: "01",
      title: "Prise de contact",
      description: "Appelez-nous ou réservez un créneau. Nous échangeons sur votre bateau : modèle, année, état général."
    },
    {
      number: "02",
      title: "Collecte d'informations",
      description: "Vous nous envoyez photos et documents (heures moteur, historique d'entretien, équipements)."
    },
    {
      number: "03",
      title: "Analyse et estimation",
      description: "Nous analysons le marché et comparons avec des ventes similaires pour établir une fourchette de prix."
    },
    {
      number: "04",
      title: "Remise du rapport",
      description: "Vous recevez votre estimation détaillée avec nos recommandations personnalisées."
    }
  ];

  const faqs = [
    {
      question: "Combien coûte une estimation de bateau ?",
      answer: "L'estimation initiale par téléphone est gratuite et sans engagement. Elle vous donne une première fourchette de prix basée sur les informations que vous nous communiquez."
    },
    {
      question: "Combien vaut mon bateau ?",
      answer: "La valeur dépend de nombreux critères : marque, modèle, année, état général, heures moteur, équipements, historique d'entretien. Une estimation professionnelle prend en compte tous ces éléments et les compare aux transactions récentes du marché."
    },
    {
      question: "Quelle est la différence avec une cote Argus ?",
      answer: "Les cotes (Argus, BUC) donnent une valeur moyenne théorique. Notre estimation est personnalisée : elle tient compte de l'état réel de VOTRE bateau, de ses équipements spécifiques et du marché local actuel."
    },
    {
      question: "Combien de temps prend une estimation ?",
      answer: "L'estimation initiale par téléphone prend 15-30 minutes. Pour une estimation détaillée avec rapport écrit, comptez 48 à 72 heures après réception des photos et documents."
    },
    {
      question: "L'estimation m'engage-t-elle à vendre ?",
      answer: "Absolument pas. L'estimation est sans engagement. Vous êtes libre de l'utiliser comme bon vous semble : pour vendre, pour assurer votre bateau à sa juste valeur, ou simplement pour connaître sa valeur actuelle."
    },
    {
      question: "Proposez-vous des services de vente ?",
      answer: "Oui, si après l'estimation vous souhaitez être accompagné pour la vente, nous proposons un service complet : mise en valeur, diffusion d'annonce, qualification des acheteurs et négociation. Contactez-nous pour en savoir plus."
    }
  ];

  return (
    <>
      <SEOHead
        title="Estimation Bateau Gratuite par un Expert | validatedboat"
        description="Obtenez une estimation gratuite et précise de votre bateau par un expert nautique. Analyse du marché, comparaison des ventes récentes et rapport détaillé."
        keywords="estimation bateau gratuite, combien vaut mon bateau, estimation voilier, prix bateau occasion, valeur bateau, cote bateau"
        canonical="https://validatedboat.fr/vendre-mon-bateau"
      />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-b from-[#1a1c1b] to-[#302f2c]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block bg-[#d9fb06] text-[#1a1c1b] px-4 py-1 rounded-full text-sm font-bold uppercase mb-6">
                Service gratuit
              </span>
              <h1 className="font-black text-4xl md:text-5xl lg:text-6xl leading-tight text-[#dfddd6] uppercase mb-6">
                <span className="text-[#d9fb06]">Estimation gratuite</span> de votre bateau par un expert
              </h1>
              <p className="text-xl text-[#888680] mb-8 leading-relaxed">
                Combien vaut votre bateau ? Obtenez une estimation précise basée sur le marché actuel, 
                l'état réel de votre bateau et les ventes récentes comparables.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a href="#estimation" className="inline-block">
                  <Button className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e205] font-bold px-8 py-4 rounded-full text-lg uppercase w-full sm:w-auto">
                    Demander mon estimation
                  </Button>
                </a>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=800" 
                alt="Estimation de bateau par un expert"
                className="rounded-2xl shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -left-6 bg-[#d9fb06] text-[#1a1c1b] p-6 rounded-xl shadow-xl">
                <p className="font-black text-3xl">100%</p>
                <p className="font-bold uppercase text-sm">Gratuit</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Bar */}
      <section className="bg-[#d9fb06] py-6">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="flex items-center justify-center gap-3">
              <Calculator className="text-[#1a1c1b]" size={28} />
              <span className="text-[#1a1c1b] font-bold uppercase">Estimation 100% gratuite</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Clock className="text-[#1a1c1b]" size={28} />
              <span className="text-[#1a1c1b] font-bold uppercase">Réponse sous 48h</span>
            </div>
            <div className="flex items-center justify-center gap-3">
              <Shield className="text-[#1a1c1b]" size={28} />
              <span className="text-[#1a1c1b] font-bold uppercase">Sans engagement</span>
            </div>
          </div>
        </div>
      </section>

      {/* Why Expert Section */}
      <section className="py-20 bg-[#1a1c1b]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-5xl leading-tight text-[#d9fb06] uppercase mb-6">
              Pourquoi faire estimer son bateau ?
            </h2>
            <p className="text-xl text-[#888680] max-w-3xl mx-auto">
              Connaître la valeur réelle de votre bateau est essentiel, que ce soit pour vendre, 
              assurer correctement ou simplement savoir où vous en êtes.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {avantages.map((avantage) => (
              <Card key={avantage.title} className="bg-[#302f2c] border-[#3f4816] p-8 hover:border-[#d9fb06] transition-all duration-300 text-center">
                <div className="bg-[#d9fb06] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <avantage.icon className="text-[#1a1c1b]" size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#d9fb06] mb-3 uppercase">{avantage.title}</h3>
                <p className="text-[#888680]">{avantage.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Section */}
      <section className="py-20 bg-[#302f2c]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-5xl leading-tight text-[#d9fb06] uppercase mb-6">
              Estimation en ligne vs Expert
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <Card className="bg-[#1a1c1b] border-[#3f4816] p-8">
              <h3 className="text-2xl font-bold text-[#888680] mb-4 uppercase">Cotes en ligne</h3>
              <ul className="space-y-4 text-[#888680]">
                <li className="flex items-start gap-3">
                  <span className="text-red-400">✗</span>
                  Valeur moyenne théorique
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400">✗</span>
                  Ne tient pas compte de l'état réel
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400">✗</span>
                  Équipements non considérés
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400">✗</span>
                  Marché local ignoré
                </li>
                <li className="flex items-start gap-3">
                  <span className="text-red-400">✗</span>
                  Aucun conseil personnalisé
                </li>
              </ul>
            </Card>

            <Card className="bg-[#1a1c1b] border-2 border-[#d9fb06] p-8">
              <h3 className="text-2xl font-bold text-[#d9fb06] mb-4 uppercase">Expert validatedboat</h3>
              <ul className="space-y-4 text-[#dfddd6]">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
                  Estimation personnalisée
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
                  Analyse de l'état réel
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
                  Équipements valorisés
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
                  Comparaison ventes récentes
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-[#d9fb06] flex-shrink-0 mt-1" size={20} />
                  Conseils pour optimiser la valeur
                </li>
              </ul>
            </Card>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="py-20 bg-[#1a1c1b]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-5xl leading-tight text-[#d9fb06] uppercase mb-6">
              Comment ça marche ?
            </h2>
            <p className="text-xl text-[#888680] max-w-3xl mx-auto">
              Un processus simple en 4 étapes pour obtenir votre estimation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {etapes.map((etape) => (
              <div key={etape.number} className="bg-[#302f2c] rounded-xl p-6 border border-[#3f4816] hover:border-[#d9fb06] transition-all duration-300 text-center">
                <div className="bg-[#d9fb06] text-[#1a1c1b] font-black text-2xl w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4">
                  {etape.number}
                </div>
                <h3 className="text-lg font-bold text-[#dfddd6] mb-2 uppercase">{etape.title}</h3>
                <p className="text-[#888680] text-sm">{etape.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-[#302f2c]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-16">
            <h2 className="font-black text-4xl md:text-5xl leading-tight text-[#d9fb06] uppercase mb-6">
              Questions fréquentes
            </h2>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {faqs.map((faq) => (
              <Card key={faq.question} className="bg-[#1a1c1b] border-[#3f4816] p-6 md:p-8">
                <h3 className="text-xl font-bold text-[#d9fb06] mb-4">{faq.question}</h3>
                <p className="text-[#dfddd6] leading-relaxed">{faq.answer}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA / Estimation Section */}
      <section id="estimation" className="py-20 bg-[#1a1c1b]">
        <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
          <div className="text-center mb-12">
            <h2 className="font-black text-4xl md:text-5xl leading-tight text-[#d9fb06] uppercase mb-6">
              Demandez votre estimation gratuite
            </h2>
            <p className="text-xl text-[#888680] max-w-3xl mx-auto">
              Remplissez le formulaire ou réservez un appel avec un expert.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Estimation Form */}
            <EstimationForm />

            {/* Calendly Widget — driven by admin config */}
            <div>
              <h3 className="text-2xl font-bold text-[#d9fb06] mb-6 uppercase text-center">Ou réservez un appel</h3>
              <div className="bg-[#302f2c] border-2 border-[#d9fb06] rounded-xl overflow-hidden" data-testid="vendre-calendly-container">
                {calendlyUrl ? (
                  <InlineWidget
                    url={calendlyUrl}
                    styles={{ height: '550px', minWidth: '320px' }}
                  />
                ) : (
                  <div className="p-8 text-center" data-testid="vendre-calendly-fallback">
                    <Mail className="mx-auto text-[#d9fb06] mb-3" size={40} />
                    <p className="text-[#dfddd6] text-sm mb-4">
                      {configLoaded
                        ? "La prise de rendez-vous en ligne est temporairement désactivée. Écrivez-moi pour convenir d'un créneau."
                        : "Chargement…"}
                    </p>
                    {configLoaded && (
                      <a
                        href="mailto:contact@validatedboat.fr?subject=Estimation bateau — demande d'appel"
                        className="inline-block bg-[#d9fb06] text-[#1a1c1b] rounded-full px-6 py-2.5 text-sm font-bold uppercase"
                      >
                        Écrire un email
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default EstimationBateau;
